'use client';

import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { std } from '@funkey/shared/utils';
import { I18N_CONF } from '@funkey/locales/config';
import type { IconName } from '~/components/primitives';
import { NumberInput } from '~/components/primitives';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useTRPC } from '~/modules/trpc/client';
import type { TRPC_ROUTER } from '~/modules/trpc/routers';
import { ACTIVITY_FILTER_RULES } from '~/features/activities/filterRules';
import { useI18n } from '~/features/i18n';
import { useStateObject } from '~/utils/react';
import { cx } from 'class-variance-authority';

type TranslationFn = (i18n: ReturnType<typeof useI18n>) => string;
export type ActivityFilterConfig<
    RelevantFilterState extends Record<string, unknown>,
    Data = undefined,
> = {
    /** A ghost property used for type inferrence */
    $relevantFilterState?: RelevantFilterState;

    key: string;

    label: (i18n: ReturnType<typeof useI18n>) => string;
    /** Gets rendered when `selectionDisplay` **equals** or **returns** `undefined` */
    placeholder?: (i18n: ReturnType<typeof useI18n>) => string;
    /** A function that displays the filter state.
     * If it **equals** or **returns** `undefined`, the `placeholder` is rendered */
    selectionDisplay?: (
        i18n: ReturnType<typeof useI18n>,
        value: RelevantFilterState,
        data: Data,
        renderLocation: 'desktop' | 'mobile',
    ) => string | undefined;

    /** A component that renders the filter CRUD controls.
     *
     * Render locations:
     *  - **desktop**: popup when filter button is clicked
     *  - **mobile**: in the filter modal when this filter is not collapsed */
    PopupContent: React.FC<{
        i18n: ReturnType<typeof useI18n>;
        /** The state of all the filters */
        value: RelevantFilterState;
        /** The set state action that can mutate all filter states. */
        onChange: React.Dispatch<React.SetStateAction<RelevantFilterState>>;
        data: Data;
    }>;

    /** Some extra info about the filter. */
    footNote?: TranslationFn;

    /** Optional width/styling override for the desktop popover. Filters default to a
     *  narrow popover (≤300px); the location filter opts into a wider one. */
    popoverClassName?: string;

    /** Data only used in the mobile view */
    mobile: {
        /** The icon that gets rendered in the button that opens the modal */
        icon: IconName;
        /** The label that gets rendered in the button that opens the modal */
        ctaText: TranslationFn;

        /** A mobile version override */
        Component?: React.FC<{
            i18n: ReturnType<typeof useI18n>;
            /** The state of all the filters */
            value: RelevantFilterState;
            /** The set state action that can mutate all filter states. */
            onChange: React.Dispatch<React.SetStateAction<RelevantFilterState>>;
            data: Data;
        }>;
    };
} & (Exclude<Data, undefined> extends never
    ? { useDataLoader?: never }
    : {
          /** A hook that fetches data for the filter.
           *
           * This data is also passed ot the `selectionDisplay` function.
           * This is why this is a separate function instead of a hook called in the `PopupContent` */
          useDataLoader: (filterState: Record<string, unknown>) => Data;
      });

function useDeferredSync<T>(stateValue: T, onSync: (newValue: T) => void) {
    const deferredValue = useDeferredValue(stateValue);

    useEffect(() => {
        onSync(deferredValue);
    }, [deferredValue]);

    return deferredValue;
}
export const NumberRangeFilter: React.FC<{
    min: {
        value: number | undefined;
        onChange: (newMin: number | undefined) => void;
    };
    max: {
        value: number | undefined;
        onChange: (newMax: number | undefined) => void;
    };
    className?: string;
    separator?: React.ReactNode;
    prefix?: string;
    hideButtons?: boolean;
    externalButtons?: boolean;
}> = props => {
    const min = useStateObject(props.min.value);
    useDeferredSync(min.value, props.min.onChange);

    const max = useStateObject(props.max.value);
    useDeferredSync(max.value, props.max.onChange);

    return (
        <div className={`flex items-center justify-between ${props.className}`}>
            <NumberInput
                className="mb-0!"
                value={min.value}
                placeholder="0"
                min={0}
                // TODO: check why this can be undefined
                onChange={(newMin, source) => {
                    // Ensures that min will never be >= max when min is incremented
                    if (
                        source !== 'INPUT' &&
                        newMin !== undefined &&
                        newMin > 0 &&
                        newMin + 1 > (max.value ?? 0)
                    ) {
                        max.set(newMin + 1);
                    }

                    min.set(newMin);
                }}
                step={1}
                hideButtons={props.hideButtons}
                externalButtons={props.externalButtons}
            />
            {props?.separator}
            <NumberInput
                className="mb-0!"
                value={max.value}
                placeholder="0"
                min={0}
                // TODO: check why this can be undefined
                onChange={(newMax, source) => {
                    // Ensures that min will never be >= max when max is decremented
                    if (
                        source !== 'INPUT' &&
                        newMax !== undefined &&
                        min.value !== undefined &&
                        min.value > 0 &&
                        min.value + 1 > (max.value ?? 0)
                    ) {
                        min.set(newMax - 1);
                    }

                    max.set(newMax);
                }}
                step={1}
                hideButtons={props.hideButtons}
                externalButtons={props.externalButtons}
            />
        </div>
    );
};

export function displayFirstN(items: (string | undefined | null)[], n: number) {
    const firstN = items.reduce<string[]>((acc, x) => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        if (acc.length !== n && Boolean(x)) acc.push(x!);
        return acc;
    }, []);

    let display = firstN.join(', ');
    const nResting = items.length - firstN.length;

    if (nResting > 0) {
        display += ` +${nResting}`;
    }

    return display;
}

const FILTER_DATA_STALE_TIME = 1000 * 60 * 60 * 24;

/** Full region universe for a country, translated to the active locale.
 * Defaults to the locale's country. Unfiltered — browse-hiding by the rules
 * atom lives in {@link useBrowseRegions}; the edit-mode rules picker reads this
 * directly so the full universe stays toggleable. */
export function useRegionsQuery(country?: I18N_CONF.Country) {
    const i18n = useI18n();
    const trpc = useTRPC();
    const query = useQuery(
        trpc.regions.byCountryCode.queryOptions(
            { countryCode: country ?? i18n.country, locale: i18n.locale },
            { staleTime: FILTER_DATA_STALE_TIME },
        ),
    );

    return { isLoading: query.isLoading, regions: query.data };
}

/** Full activity-language universe, translated to the active locale. Languages
 * are never constrained by catalog rules, so there is no browse variant. */
export function useLanguagesQuery() {
    const i18n = useI18n();
    const trpc = useTRPC();
    const query = useQuery(
        trpc.languages.queryOptions({ locale: i18n.locale }, { staleTime: FILTER_DATA_STALE_TIME }),
    );

    return { isLoading: query.isLoading, languages: query.data };
}

/** A category with its subcategories nested under `children`. `isRoot` (no
 * parent) and `isCustom` (tenant-specific) are derived from the flat
 * `forPortal` rows. The returned list is flat (roots *and* subcategories) so
 * callers can iterate everything or pick roots and read `children`. */
export type ActivityFilterCategory = {
    id: number;
    name: string;
    filterName: string | null;
    parentId: number | null;
    tenantIds: readonly number[];
    isRoot: boolean;
    isCustom: boolean;
    children: ActivityFilterCategory[];
};

function buildActivityCategoryTree(
    rows: TRPC_ROUTER.Outputs['categories']['forPortal'],
): ActivityFilterCategory[] {
    const nodes = rows.map<ActivityFilterCategory>(row => ({
        id: row.id,
        name: row.name ?? '',
        filterName: row.filterName ?? null,
        parentId: row.parentId,
        tenantIds: row.tenantIds,
        isRoot: row.parentId === null,
        isCustom: row.tenantIds.length > 0,
        children: [],
    }));

    const byId = new Map(nodes.map(node => [node.id, node]));
    for (const node of nodes) {
        if (node.parentId !== null) byId.get(node.parentId)?.children.push(node);
    }

    const byName = (a: ActivityFilterCategory, b: ActivityFilterCategory) =>
        a.name.localeCompare(b.name);
    for (const node of nodes) node.children.sort(byName);

    return nodes.sort(byName);
}

/** Full category universe for the portal (public categories + the passed
 * tenant's categories), translated and nested. Unfiltered — browse-hiding by
 * the rules atom lives in {@link useBrowseCategories}; the edit-mode rules
 * picker reads this directly so every root stays toggleable. */
export function useCategoriesQuery(opts?: { tenantId?: number | null }) {
    const i18n = useI18n();
    const trpc = useTRPC();
    const query = useQuery(
        trpc.categories.forPortal.queryOptions(
            { locale: i18n.locale, tenantId: opts?.tenantId ?? undefined },
            { staleTime: FILTER_DATA_STALE_TIME },
        ),
    );

    return {
        isLoading: query.isLoading,
        categories: useMemo(
            () => (query.data ? buildActivityCategoryTree(query.data) : undefined),
            [query.data],
        ),
    };
}

/** Intersects two allowlists, treating `null` as "no constraint = all". The
 * rules atom (BIZZ browse) and the search-customization `allowedCategories`
 * (WEB category-landing pages) each gate options independently. */
function intersectAllowlist(
    a: readonly number[] | null,
    b: readonly number[] | null,
): readonly number[] | null {
    if (a === null) return b;
    if (b === null) return a;
    const inB = new Set(b);
    return a.filter(id => inB.has(id));
}

/** {@link useRegionsQuery} with the rules atom applied: in BIZZ browse this
 * hides regions outside the catalog allowlist; portals without rules read the
 * atom default (`null`) and see everything. */
export function useBrowseRegions(country?: I18N_CONF.Country) {
    const query = useRegionsQuery(country);
    const allowed = useAtomValue(ACTIVITY_FILTER_RULES).activityFilters.activityRegionIDs;

    return {
        isLoading: query.isLoading,
        regions: useMemo(() => {
            if (!query.regions || allowed === null) return query.regions;
            const allowedSet = new Set(allowed);
            return query.regions.filter(region => allowedSet.has(region.id));
        }, [query.regions, allowed]),
    };
}

/** {@link useCategoriesQuery} with both option-gates applied: the rules atom
 * (BIZZ browse) and `allowedCategories` (WEB category pages). A root survives
 * if it's in the combined allowlist; a subcategory survives with its root. */
export function useBrowseCategories(opts: {
    tenantId?: number | null;
    allowedCategories?: readonly number[] | null;
}) {
    const query = useCategoriesQuery({ tenantId: opts.tenantId });
    const ruleAllowed = useAtomValue(ACTIVITY_FILTER_RULES).activityFilters.rootCategoryIDs;

    return {
        isLoading: query.isLoading,
        categories: useMemo(() => {
            const allowedRoots = intersectAllowlist(ruleAllowed, opts.allowedCategories ?? null);
            if (!query.categories || allowedRoots === null) return query.categories;
            const allowedSet = new Set(allowedRoots);
            return query.categories.filter(category =>
                category.isRoot
                    ? allowedSet.has(category.id)
                    : category.parentId !== null && allowedSet.has(category.parentId),
            );
        }, [query.categories, ruleAllowed, opts.allowedCategories]),
    };
}

export const ActivityFilterBar_Mobile_SelectFilter: React.FC<{
    value: string | null;
    onChange: (value: string | null) => void;
    placeholder: React.ReactNode;
    selectNoneText?: React.ReactNode;
    /** Place `<option/>` elements here */
    options: Array<{ value: string; label: string }>;
}> = props => {
    const OPTION_NONE_SELECTED = '';

    return (
        <select
            value={props.value ?? OPTION_NONE_SELECTED}
            disabled={props.options.length === 0}
            onChange={e => {
                const optionID = e.currentTarget.value;
                props.onChange(optionID === OPTION_NONE_SELECTED ? null : optionID);
            }}
            className={cx(
                'outline-hidden absolute inset-0 min-h-14 w-full appearance-none border-0 border-none bg-transparent pb-2 pl-12 pr-4 pt-2 text-sm font-medium focus:ring-0',
                props.value === null ? 'text-blue-500' : 'text-primary',
            )}
        >
            <option value={OPTION_NONE_SELECTED} disabled hidden>
                {props.placeholder}
            </option>
            {props.options.length && (
                <option value={OPTION_NONE_SELECTED}>
                    {props.selectNoneText ?? props.placeholder}
                </option>
            )}
            {props.options.map(x => (
                <option key={x.value} value={x.value}>
                    {x.label}
                </option>
            ))}
        </select>
    );
};

export const ActivityFilterBar_Mobile_NumberFilter: React.FC<{
    value: number | null;
    prefix?: React.ReactNode;
    suffix?: React.ReactNode;
    onChange(value: number | null): void;
    placeholder: string;
}> = props => {
    const [inputValue, setInputValue] = useState(
        () => props.value?.toLocaleString(I18N_CONF.DEFAULT_LOCALE) ?? '',
    );
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        if (isFocused) return;
        setInputValue(props.value?.toLocaleString(I18N_CONF.DEFAULT_LOCALE) ?? '');
    }, [props.value, isFocused]);

    function onBlur(e: React.SyntheticEvent<HTMLInputElement>) {
        setIsFocused(false);

        props.onChange(
            (() => {
                let str = e.currentTarget.value.replaceAll('.', '').replace(',', '.');
                if (str.length === 0) return null;
                if (str.endsWith('.')) str = str.slice(0, str.length - 1);

                return std.Number.safeParseFloat(str);
            })(),
        );
    }

    return (
        <div className="flex h-full min-h-14 w-full items-center gap-2 self-stretch">
            {!!inputValue && !!props.prefix && (
                <span className="text-gray-400">{props.prefix}</span>
            )}
            <input
                type="text"
                inputMode="decimal"
                className={cx(
                    // h-full + self-stretch so the input fills the whole row height,
                    // giving a tap target that covers the full card (not just the text line).
                    'outline-hidden h-full min-h-14 w-full self-stretch border-none bg-transparent p-0 text-sm font-medium focus:ring-0',
                    !inputValue ? 'placeholder:text-blue-500' : 'text-primary',
                )}
                value={inputValue}
                placeholder={props.placeholder}
                onChange={e => setInputValue(e.currentTarget.value)}
                onFocus={() => setIsFocused(true)}
                onBlur={onBlur}
                onKeyDown={e => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        onBlur(e);
                    } else if (e.key.length > 1) {
                        // Allow special keys
                    } else if (Number.isNaN(Number(e.key)) && e.key !== ',' && e.key !== '.') {
                        e.preventDefault();
                    }
                }}
            />

            {!!inputValue && !!props.suffix && (
                <span className="text-gray-400">{props.suffix}</span>
            )}
        </div>
    );
};
